import type { UUID } from '../../types/common';
import type { ListEvent } from './Checklist.types';

// ---------------------------------------------------------------------------
// Projection inputs
// ---------------------------------------------------------------------------

/**
 * Inputs extracted from the event stream that are needed to resolve
 * dependencies before the main reduction step.
 *
 * - `closeDate`: the effective close date of the list. When non-null,
 *   dependent templates are frozen at that point in time.
 * - `dependencyIds`: template list IDs referenced by EXPAND_PROTO events.
 */
export interface ListProjectionInputs {
  closeDate: Date | null;
  dependencyIds: UUID[];
}

// ---------------------------------------------------------------------------
// analyzeListEvents
// ---------------------------------------------------------------------------

/**
 * Performs a lightweight scan of the event stream to extract projection
 * inputs without fully reducing the tree.
 *
 * Close/open logic: the *last* CLOSE_LIST or OPEN_LIST event wins.
 * - CLOSE_LIST sets closeDate to the event's created timestamp.
 * - OPEN_LIST clears it back to null.
 *
 * Dependency IDs are collected from all EXPAND_PROTO events (deduplicated).
 */
export function analyzeListEvents(events: ListEvent[]): ListProjectionInputs {
  let closeDate: Date | null = null;
  const dependencyIdSet = new Set<string>();

  for (const event of events) {
    switch (event.type) {
      case 'CLOSE_LIST':
        closeDate = event.created;
        break;
      case 'OPEN_LIST':
        closeDate = null;
        break;
      case 'EXPAND_PROTO':
        dependencyIdSet.add(event.payload.protoListId);
        break;
    }
  }

  return {
    closeDate,
    dependencyIds: Array.from(dependencyIdSet),
  };
}
